import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/download";
import { AlertTriangle, Package, Download } from "lucide-react";

export default function ShortageReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shortage-report").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Layout
      title="Raw Material Shortage Report"
      subtitle="Aggregated raw materials required across all active POs (Received / Acknowledged / In Production)"
      actions={
        <button onClick={() => downloadCsv("/export/shortage-report", "shortage_report.csv")} data-testid="export-shortage-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
          <Download size={14} /> Export CSV
        </button>
      }
    >
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-gray-500">Calculating...</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center" data-testid="shortage-empty">
            <Package className="mx-auto text-gray-300 mb-3" size={32} />
            <div className="font-display text-lg font-semibold text-slate-900">Nothing to procure right now</div>
            <div className="text-sm text-gray-500 mt-1 max-w-md mx-auto">Either no active POs exist, or all their line items are missing BOM data. Save BOMs from PO detail pages to see aggregated demand here.</div>
          </div>
        ) : (
          <table className="w-full" data-testid="shortage-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Material</th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Spec</th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-right">Required Qty</th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Unit</th>
                <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Used in POs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.material}|${r.spec}|${r.unit}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-slate-900 font-medium">{r.material}</td>
                  <td className="py-3 px-4 text-sm text-gray-600 font-mono">{r.spec || "—"}</td>
                  <td className="py-3 px-4 text-sm text-right font-mono text-slate-900">{Number(r.required_quantity).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                  <td className="py-3 px-4 text-sm text-gray-600 font-mono">{r.unit}</td>
                  <td className="py-3 px-4 text-xs text-gray-600">
                    <div className="flex flex-wrap gap-1">
                      {(r.used_in_pos || []).map((po) => (
                        <span key={po} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border bg-gray-50 text-gray-700 border-gray-200 font-mono">{po}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mt-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md p-4 text-amber-900">
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">Tip</div>
            <div className="text-xs mt-0.5">Quantities above are gross demand across active POs. Subtract your current stock to get the true procurement quantity.</div>
          </div>
        </div>
      )}
    </Layout>
  );
}
